import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MapPin, Briefcase, Clock, ArrowLeft } from 'lucide-react';
import { api } from '../../lib/api';
import {
  type Slot,
  type SlotsResponse,
  browserTz,
  formatSlotLocal,
} from '../../lib/slots';
import SlotPicker from '../../components/SlotPicker';
import {
  type MeetingType,
  MeetingTypeBadge,
  MeetingTypePicker,
  offeredModes,
} from '../../lib/meetingType';

type BookingRow = {
  id: string;
  start_time: string;
  end_time: string;
  meeting_link: string | null;
  bookee_email: string | null;
  confirmation_message: string | null;
};

/**
 * Splits an admin-authored message on http(s) URLs so we can render them as
 * anchors. Whitespace (including newlines) is preserved via the wrapping
 * `pre-wrap` on the parent.
 */
function renderConfirmationText(text: string): React.ReactNode {
  const parts = text.split(/(\bhttps?:\/\/[^\s)]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

type StaffService = {
  id: string;
  name: string;
  slug: string;
  duration_minutes: number;
  description: string | null;
  offers_in_person: number;
  offers_phone: number;
  offers_video: number;
};

type StaffLocation = {
  id: string;
  name: string;
  slug: string;
  address: string;
  timezone: string;
  is_default?: number;
};

type OrgBrand = {
  name: string;
  brand_name: string | null;
  brand_logo_url: string | null;
};

type StaffProfile = {
  id: string;
  name: string;
  slug: string;
  bio: string | null;
  avatar_url: string | null;
  timezone: string;
  services: StaffService[];
  locations: StaffLocation[];
  org: OrgBrand;
};

export default function BookPage() {
  const { orgSlug, staffSlug } = useParams();
  const [params, setParams] = useSearchParams();
  const service = params.get('service');
  const location = params.get('location');

  const tz = useMemo(browserTz, []);

  // Staff profile is fetched once we have org+staff slugs. Used both for the
  // pre-picker (when no service/location query yet) and for the header above
  // the slot grid (so we can show the staff name + service title).
  const [profile, setProfile] = useState<StaffProfile | null>(null);
  const [profileErr, setProfileErr] = useState<string | null>(null);

  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const [err, setErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<Slot | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState<BookingRow | null>(null);
  const [bookee, setBookee] = useState({ name: '', email: '', phone: '', notes: '' });
  // Which meeting mode the customer picked (defaults to the service's first
  // offered mode once a service is chosen). Sent with the confirm.
  const [meetingMode, setMeetingMode] = useState<MeetingType | null>(null);

  // Local-only picks before they get pushed into the query string. Default
  // location to the staff's is_default if present.
  const [picksLoc, setPicksLoc] = useState<string | null>(null);
  const [picksSvc, setPicksSvc] = useState<string | null>(null);

  useEffect(() => {
    if (!orgSlug || !staffSlug) return;
    api<StaffProfile>(`/book/${orgSlug}/staff/${staffSlug}`)
      .then((p) => {
        setProfile(p);
        // Initialize the local picker with the staff's default location (and
        // sole service if there's only one).
        const def = p.locations.find((l) => l.is_default) ?? p.locations[0];
        if (def) setPicksLoc(def.slug);
        if (p.services.length === 1) setPicksSvc(p.services[0]!.slug);
        // If the staff has exactly 1 of each and the query string is empty,
        // auto-promote them to the URL so we skip straight to slot picking.
        if (!service && !location && p.services.length === 1 && p.locations.length === 1) {
          const next = new URLSearchParams(params);
          next.set('service', p.services[0]!.slug);
          next.set('location', p.locations[0]!.slug);
          setParams(next, { replace: true });
        }
      })
      .catch((e) => setProfileErr((e as Error).message || 'load_failed'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSlug, staffSlug]);

  // Default the meeting mode to the chosen service's first offered mode.
  useEffect(() => {
    if (!profile || !service) return;
    const svc = profile.services.find((s) => s.slug === service);
    const modes = svc ? offeredModes(svc) : [];
    setMeetingMode(modes[0] ?? null);
  }, [profile, service]);

  useEffect(() => {
    if (!orgSlug || !staffSlug || !service || !location) return;
    // Ask for slots starting *now* (not midnight UTC) so the backend never
    // returns slots that already started today. Even with this, the FE also
    // re-filters at render time in case the page sits open long enough for
    // the earliest visible slot to slip into the past.
    const now = new Date();
    const from = now.toISOString();
    const to = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString();
    api<SlotsResponse>(
      `/book/${orgSlug}/staff/${staffSlug}/slots?service=${encodeURIComponent(service)}&location=${encodeURIComponent(location)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    )
      .then((r) => {
        setSlots(r.slots);
        setDuration(r.duration_minutes);
      })
      .catch((e) => setErr(e.message || 'load_failed'));
  }, [orgSlug, staffSlug, service, location]);

  async function submit() {
    if (!picked || !service || !location || !orgSlug || !staffSlug) return;
    if (!bookee.name.trim()) {
      setErr('name_required');
      return;
    }
    if (meetingMode === 'phone_call' && !bookee.phone.trim()) {
      setErr('phone_required');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const row = await api<BookingRow>(`/book/${orgSlug}/staff/${staffSlug}/confirm`, {
        body: {
          service,
          location,
          start_time: picked.startUtc,
          timezone: tz,
          meeting_type: meetingMode ?? undefined,
          bookee: {
            name: bookee.name,
            email: bookee.email || null,
            phone: bookee.phone || null,
          },
          notes: bookee.notes || null,
        },
      });
      setConfirmed(row);

      if (window.parent !== window) {
        window.parent.postMessage(
          {
            type: 'invitey:booking-confirmed',
            payload: {
              booking_id: row.id,
              start_time: row.start_time,
              end_time: row.end_time,
              meeting_link: row.meeting_link,
            },
          },
          '*',
        );
      }
    } catch (e) {
      setErr((e as Error).message || 'submit_failed');
    } finally {
      setSubmitting(false);
    }
  }

  // Advance to slot-picking. Clicking a service passes its slug directly (so we
  // don't race the picksSvc state update); location uses the current pick, which
  // defaults to the staff's default/first location.
  function startBooking(svcSlug?: string) {
    const svc = svcSlug ?? picksSvc;
    if (!picksLoc || !svc) return;
    const next = new URLSearchParams(params);
    next.set('service', svc);
    next.set('location', picksLoc);
    setParams(next);
    // Reset downstream state so any stale slot list from a previous attempt
    // is cleared and the slots-fetch effect runs fresh.
    setSlots(null);
    setPicked(null);
    setErr(null);
  }

  function changePicks() {
    const next = new URLSearchParams(params);
    next.delete('service');
    next.delete('location');
    setParams(next);
    setSlots(null);
    setPicked(null);
    setErr(null);
  }

  // ----- Render branches ---------------------------------------------------

  if (profileErr === 'not_found') {
    return <h1>Page not found</h1>;
  }
  if (profileErr && !profile) {
    return <p className="err">Error: {profileErr}</p>;
  }
  if (!profile) {
    return <p className="muted">Loading…</p>;
  }

  // Pre-step: no service/location yet — show staff profile + pickers.
  if (!service || !location) {
    const noServices = profile.services.length === 0;
    const noLocations = profile.locations.length === 0;
    return (
      <div className="book-staff">
        <StaffHeader profile={profile} />

        {noServices || noLocations ? (
          <div className="card">
            <p className="muted">
              {profile.name} isn&rsquo;t accepting bookings right now.{' '}
              {noServices && noLocations
                ? 'No services or locations are configured yet.'
                : noServices
                  ? 'No services are configured yet.'
                  : 'No locations are configured yet.'}
            </p>
          </div>
        ) : (
          <>
            {/* Location comes first: picking a service immediately advances to
                available times, so the location must already be chosen. */}
            {profile.locations.length > 1 ? (
              <div className="card">
                <h2 style={{ marginTop: 0 }}>
                  <MapPin size={16} /> Location
                </h2>
                <div className="pick-grid">
                  {profile.locations.map((l) => (
                    <button
                      key={l.id}
                      className={`pick-card ${picksLoc === l.slug ? 'pick-card-active' : ''}`}
                      onClick={() => setPicksLoc(l.slug)}
                    >
                      <div className="pick-card-title">{l.name}</div>
                      <div className="pick-card-meta">{l.address}</div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              profile.locations[0] && (
                <p className="muted">
                  <MapPin size={14} style={{ verticalAlign: '-2px' }} />{' '}
                  {profile.locations[0].name} · {profile.locations[0].address}
                </p>
              )
            )}

            <div className="card">
              <h2 style={{ marginTop: 0 }}>
                <Briefcase size={16} /> Choose a service
              </h2>
              <div style={{ display: 'grid', gap: '0.6rem' }}>
                {profile.services.map((s) => (
                  <button
                    key={s.id}
                    className="pick-card"
                    onClick={() => startBooking(s.slug)}
                    disabled={!picksLoc}
                  >
                    <div className="pick-card-title">{s.name}</div>
                    <div className="pick-card-meta">
                      <Clock size={12} /> {s.duration_minutes} min
                    </div>
                    <div
                      style={{ marginTop: '0.3rem', display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}
                    >
                      {offeredModes(s).map((m) => (
                        <MeetingTypeBadge key={m} type={m} />
                      ))}
                    </div>
                    {s.description && <p className="muted small">{s.description}</p>}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Confirmation — the booking's done, so keep it lean: no staff header/bio or
  // logo (they've served their purpose earlier in the flow).
  if (confirmed) {
    const brandLabel = profile.org.brand_name ?? profile.org.name;
    return (
      <div>
        <div className="card">
          <h1>You&apos;re booked with {profile.name}</h1>
          <p>
            {formatSlotLocal(confirmed.start_time, tz)} — confirmation sent to{' '}
            <strong>{confirmed.bookee_email ?? 'your contact info'}</strong>.
          </p>
          {confirmed.meeting_link && (
            <p>
              Join link: <a href={confirmed.meeting_link}>{confirmed.meeting_link}</a>
            </p>
          )}
          {confirmed.confirmation_message && (
            <div
              style={{
                marginTop: '1rem',
                padding: '0.85rem 1rem',
                background: 'var(--color-surface-alt)',
                borderRadius: 'var(--radius-sm)',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5,
              }}
            >
              {renderConfirmationText(confirmed.confirmation_message)}
            </div>
          )}
          <p className="muted" style={{ marginTop: '1.25rem', fontSize: '0.85rem' }}>
            Booking handled by {brandLabel} via Invitey.
          </p>
        </div>
      </div>
    );
  }

  if (err && !slots) {
    // `not_bookable` from the slots endpoint means the (staff, location,
    // service) triple isn't fully joined in the DB. Most common cause:
    // the service isn't flagged as offered at this location yet. Surface
    // copy that's useful to a bookee rather than a server error code.
    const friendly =
      err === 'not_bookable'
        ? `This combination of staff, service, and location isn't set up for booking yet. Please contact the team to let them know — or try a different service or location.`
        : `Error: ${err}`;
    return (
      <div>
        <StaffHeader profile={profile} />
        <div className="book-context">
          <button className="ghost small" onClick={changePicks}>
            <ArrowLeft size={12} /> Change picks
          </button>
        </div>
        <p className={err === 'not_bookable' ? 'muted' : 'err'}>{friendly}</p>
      </div>
    );
  }
  if (!slots) {
    return (
      <div>
        <StaffHeader profile={profile} />
        <p className="muted">Looking up open times…</p>
      </div>
    );
  }

  const chosenSvc = profile.services.find((s) => s.slug === service);
  const chosenLoc = profile.locations.find((l) => l.slug === location);
  const svcModes = chosenSvc ? offeredModes(chosenSvc) : [];

  return (
    <div>
      <StaffHeader profile={profile} />

      <div className="book-context">
        <span className="book-context-pill">
          <Briefcase size={12} /> {chosenSvc?.name ?? service}
        </span>
        <span className="book-context-pill">
          <MapPin size={12} /> {chosenLoc?.name ?? location}
        </span>
        <button className="ghost small" onClick={changePicks}>
          <ArrowLeft size={12} /> Change
        </button>
      </div>

      <h1>Pick a time</h1>
      <p className="muted">
        Times shown in <strong>{tz}</strong>. Duration: {duration} min.
      </p>

      <SlotPicker slots={slots} tz={tz} picked={picked} onPick={setPicked} />

      {picked && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Your info</h2>
          <p className="muted">Booking {formatSlotLocal(picked.startUtc, tz)}</p>
          {svcModes.length > 1 && (
            <label>
              <span>How would you like to meet?</span>
              <MeetingTypePicker
                value={meetingMode ?? svcModes[0]!}
                onChange={setMeetingMode}
                options={svcModes}
              />
            </label>
          )}
          <label>
            <span>Name *</span>
            <input
              value={bookee.name}
              onChange={(e) => setBookee({ ...bookee, name: e.target.value })}
            />
          </label>
          <div className="grid-2">
            <label>
              <span>Email</span>
              <input
                type="email"
                value={bookee.email}
                onChange={(e) => setBookee({ ...bookee, email: e.target.value })}
              />
            </label>
            <label>
              <span>Phone {meetingMode === 'phone_call' ? '*' : ''}</span>
              <input
                type="tel"
                value={bookee.phone}
                onChange={(e) => setBookee({ ...bookee, phone: e.target.value })}
              />
              {meetingMode === 'phone_call' && (
                <p className="field-help" style={{ marginBottom: 0 }}>
                  We&rsquo;ll call this number for your appointment.
                </p>
              )}
            </label>
          </div>
          <label>
            <span>Notes (optional)</span>
            <textarea
              rows={3}
              value={bookee.notes}
              onChange={(e) => setBookee({ ...bookee, notes: e.target.value })}
            />
          </label>
          {err && (
            <p className="err">
              {err === 'slot_taken'
                ? 'Someone else just grabbed that time — pick another.'
                : err === 'past_slot'
                  ? 'That time has already started — pick a future slot.'
                  : err === 'name_required'
                    ? 'Please enter your name.'
                    : err === 'phone_required'
                      ? 'A phone number is required for a phone-call booking.'
                      : `Error: ${err}`}
            </p>
          )}
          <button className="primary" onClick={submit} disabled={submitting}>
            {submitting ? 'Booking…' : 'Confirm booking'}
          </button>
        </div>
      )}
    </div>
  );
}

function StaffHeader({ profile }: { profile: StaffProfile }) {
  return (
    <div className="staff-header">
      {profile.avatar_url && (
        <img
          src={profile.avatar_url}
          alt={profile.name}
          width={56}
          height={56}
          style={{ borderRadius: '50%' }}
        />
      )}
      <div>
        <h1 style={{ margin: '0 0 0.15rem' }}>Book with {profile.name}</h1>
        {profile.bio && <p className="muted" style={{ margin: 0 }}>{profile.bio}</p>}
      </div>
    </div>
  );
}
